import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CSVExportModal from './CSVExportModal';

// ─── small reusable toggle-chip group ────────────────────────────────────────
function FilterGroup({ label, options, value, onChange, disabled = false }) {
    return (
        <div className="d-flex align-items-center gap-2">
            <span className={`text-muted small fw-semibold text-nowrap${disabled ? ' opacity-50' : ''}`}>
                {label}
            </span>
            <div className={`btn-group btn-group-sm${disabled ? ' opacity-50' : ''}`} role="group">
                {options.map(([v, display]) => (
                    <button
                        key={v}
                        type="button"
                        disabled={disabled}
                        className={`btn ${value === v && !disabled ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => !disabled && onChange(v)}
                    >
                        {display}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── pagination helper ────────────────────────────────────────────────────────
function pageWindow(current, total, size = 5) {
    const start = Math.max(1, Math.min(current - Math.floor(size / 2), total - size + 1));
    return Array.from({ length: Math.min(size, total) }, (_, i) => start + i);
}

// ─── main component ───────────────────────────────────────────────────────────
function ManagerPlayerListPage() {
    // filter state
    const [searchInput, setSearchInput]     = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterStatus, setFilterStatus]   = useState('all');
    const [selectedSeason, setSelectedSeason] = useState('all');
    const [filterPaid, setFilterPaid]       = useState('all');
    const [filterWaiver, setFilterWaiver]   = useState('all');

    // pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize]       = useState(20);
    const [totalPages, setTotalPages]   = useState(1);
    const [totalCount, setTotalCount]   = useState(0);

    // data
    const [players, setPlayers]       = useState([]);
    const [allSeasons, setAllSeasons] = useState([]);

    // ui
    const [loading, setLoading]           = useState(true);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [error, setError]               = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [initialized, setInitialized]   = useState(false);

    // ── debounce search input ────────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // ── init: lightweight call just to get the seasons list + auto-select ────
    useEffect(() => {
        let cancelled = false;
        axios.get('/api/player/', {
            params: { page: 1, page_size: 1 },
            withCredentials: true,
        }).then(res => {
            if (cancelled) return;
            const seasons = res.data.seasons || res.data.active_seasons || [];
            if (seasons.length) {
                setAllSeasons(seasons);
                const active = seasons.find(s => s.is_active);
                if (active) setSelectedSeason(active.id.toString());
            }
        }).catch(() => {
            // non-fatal – proceed without season pre-selection
        }).finally(() => {
            if (!cancelled) setInitialized(true);
        });
        return () => { cancelled = true; };
    }, []);

    // ── main fetch: re-runs whenever any filter / page changes ───────────────
    useEffect(() => {
        if (!initialized) return;

        let cancelled = false;
        setFetchLoading(true);

        const params = {
            search:    debouncedSearch,
            status:    filterStatus,
            page:      currentPage,
            page_size: pageSize,
        };
        if (selectedSeason !== 'all') params.season = selectedSeason;
        if (filterPaid !== 'all') params.paid = filterPaid;
        if (filterWaiver !== 'all' && selectedSeason !== 'all') params.waiver = filterWaiver;

        axios.get('/api/player/', { params, withCredentials: true })
            .then(res => {
                if (cancelled) return;
                const data = res.data;
                setPlayers(data.results || []);
                setTotalPages(data.total_pages || 1);
                setTotalCount(data.total_count || 0);
                const seasons = data.seasons || data.active_seasons;
                if (seasons?.length) setAllSeasons(seasons);
                setError(null);
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load players');
            })
            .finally(() => {
                if (!cancelled) {
                    setFetchLoading(false);
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [initialized, debouncedSearch, filterStatus, selectedSeason, filterPaid, filterWaiver, currentPage, pageSize]);

    // ── derived season lists ─────────────────────────────────────────────────
    const currentSeasons = allSeasons.filter(s => s.is_active);
    const pastSeasons    = allSeasons.filter(s => !s.is_active);

    // ── season change: also reset waiver filter ──────────────────────────────
    const handleSeasonChange = (e) => {
        setSelectedSeason(e.target.value);
        setFilterWaiver('all');
        setCurrentPage(1);
    };

    // ── loading / error states ───────────────────────────────────────────────
    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger mb-0">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            {/* ── header ── */}
            <div className="card-header border-0 pb-3">
                <div className="d-flex align-items-start justify-content-between">
                    <div>
                        <h5 className="card-title mb-1">
                            Players{' '}
                            <span className="text-muted fw-normal fs-6">({totalCount})</span>
                        </h5>
                        <p className="text-muted small mb-0">View and manage all players</p>
                    </div>
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowExportModal(true)}
                        title="Export to CSV"
                    >
                        <i className="bi bi-download me-1"></i>Export
                    </button>
                </div>
            </div>

            <div className="card-body pt-0">
                {/* ── search + season row ── */}
                <div className="d-flex gap-3 align-items-center flex-wrap mb-3">
                    <div className="input-group" style={{ maxWidth: 320 }}>
                        <span className="input-group-text">
                            <i className="bi bi-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name or email…"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            autoComplete="off"
                        />
                        {fetchLoading && (
                            <span className="input-group-text">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading…</span>
                                </div>
                            </span>
                        )}
                    </div>

                    <select
                        className="form-select"
                        style={{ minWidth: 200, maxWidth: 280 }}
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                    >
                        <option value="all">All Seasons</option>
                        {currentSeasons.length > 0 && (
                            <optgroup label="Current">
                                {currentSeasons.map(s => (
                                    <option key={s.id} value={s.id.toString()}>
                                        {s.name} — {s.league__name}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                        {pastSeasons.length > 0 && (
                            <optgroup label="Previous">
                                {pastSeasons.map(s => (
                                    <option key={s.id} value={s.id.toString()}>
                                        {s.name} — {s.league__name}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                </div>

                {/* ── filter chips ── */}
                <div className="d-flex gap-4 align-items-center flex-wrap py-3 mb-4 border-top border-bottom">
                    <FilterGroup
                        label="Status"
                        value={filterStatus}
                        onChange={v => { setFilterStatus(v); setCurrentPage(1); }}
                        options={[['all','All'], ['available','Available'], ['on_team','On Team']]}
                    />
                    <FilterGroup
                        label="Payment"
                        value={filterPaid}
                        onChange={v => { setFilterPaid(v); setCurrentPage(1); }}
                        options={[['all','All'], ['paid','Paid'], ['unpaid','Unpaid']]}
                    />
                    <div className="d-flex align-items-center gap-2">
                        <FilterGroup
                            label="Waiver"
                            value={filterWaiver}
                            onChange={v => { setFilterWaiver(v); setCurrentPage(1); }}
                            options={[['all','All'], ['signed','Signed'], ['unsigned','Pending']]}
                            disabled={selectedSeason === 'all'}
                        />
                        {selectedSeason === 'all' && (
                            <span className="text-muted small fst-italic">select a season</span>
                        )}
                    </div>
                </div>

                {/* ── player grid ── */}
                <div className="row g-3">
                    {players.map(player => (
                        <div key={player.id} className="col-sm-6 col-lg-4">
                            <div className="card h-100">
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <img
                                            src={player.profile_picture || '/static/images/kb_static/lemont_rec_leagues_circle.png'}
                                            alt=""
                                            className="rounded-circle flex-shrink-0"
                                            style={{ width: 48, height: 48, objectFit: 'cover' }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <div className="fw-semibold text-truncate">
                                                <Link
                                                    to={`/league-manager/players/${player.user.id}/`}
                                                    className="text-decoration-none"
                                                >
                                                    {player.first_name} {player.last_name}
                                                </Link>
                                            </div>
                                            <div className="text-muted small text-truncate">{player.email}</div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-1">
                                        <span className={`badge ${player.team ? 'bg-primary' : 'bg-success'}`}>
                                            {player.team ? player.team.name : 'Available'}
                                        </span>

                                        {player.waiver_status != null && (
                                            <span className={`badge ${player.waiver_status.has_waiver ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                {player.waiver_status.has_waiver ? 'Waiver ✓' : 'Waiver Pending'}
                                            </span>
                                        )}

                                        {!player.paid && (
                                            <span className="badge bg-warning text-dark">Unpaid</span>
                                        )}

                                        {player.seasons?.map(s => (
                                            <span key={s.id} className="badge bg-light text-secondary border">
                                                {s.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {players.length === 0 && (
                        <div className="col-12 text-center text-muted py-5">
                            No players match the current filters.
                        </div>
                    )}
                </div>

                {/* ── pagination + per-page ── */}
                <div className="d-flex align-items-center justify-content-between mt-4 flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <label className="text-muted small mb-0">Show</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            {[5, 10, 20, 50, 100].map(n => (
                                <option key={n} value={n}>{n} per page</option>
                            ))}
                        </select>
                        {totalCount > 0 && (
                            <span className="text-muted small">
                                {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                            </span>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                                </li>
                                {pageWindow(currentPage, totalPages).map(n => (
                                    <li key={n} className={`page-item ${currentPage === n ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>›</button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>

            <CSVExportModal
                show={showExportModal}
                handleClose={() => setShowExportModal(false)}
                activeSeasons={allSeasons}
                selectedSeason={selectedSeason}
            />
        </div>
    );
}

export default ManagerPlayerListPage;
